/**
 * UnifiedReportDashboard.jsx
 *
 * Uses unified /UnifiedReport/* endpoints (localhost:3001).
 * All date filtering is done client-side since the API returns full tables.
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 * DATE COLUMN REFERENCE (for filtering and counting)
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * PRODUCTION (counting produced parts):
 *   - FILTER BY: Date column
 *   - HEALTH MONITOR: Schedule_Date_Time / Production_Date_Time (whichever more recent)
 * 
 * ATTENDANCE:
 *   - FILTER BY: Date column
 *   - HEALTH MONITOR: Date column
 * 
 * PO_TABLE:
 *   - FILTER BY: Timestamp column
 *   - HEALTH MONITOR: Timestamp column
 * 
 * RECEIVED_RAW_MATERIAL:
 *   - FILTER BY: Receiving_Date column
 *   - HEALTH MONITOR: Receiving_Date column
 * 
 * RECEIVED_SUPPLIES:
 *   - FILTER BY: Receiving_Date column
 *   - HEALTH MONITOR: Receiving_Date column
 * 
 * OTHER_EXPENSES:
 *   - FILTER BY: INVOICE_DATE (fallback to DATE if null)
 *   - HEALTH MONITOR: INVOICE_DATE (fallback to DATE if null)
 * 
 * SHIFT_REPORTS:
 *   - FILTER BY: date column
 *   - HEALTH MONITOR: date column
 * 
 * MAINTENANCE_REPORTS:
 *   - FILTER BY: date column
 *   - HEALTH MONITOR: date column
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect, useMemo } from 'react';
import Axios from 'axios';
import './UnifiedReportDashboard.css';

// ─── CONFIG ──────────────────────────────────────────────────────────────────
const API = 'http://localhost:3001/UnifiedReport';
const getToken = () => window.localStorage.getItem('acessToken');
const getUser = () => (window.localStorage.getItem('Name') || '').replace(/[{""}]/g, '');

// ─── PALETTE ─────────────────────────────────────────────────────────────────
const P = ['#4f9eff', '#34d399', '#f59e0b', '#f87171', '#a78bfa', '#fb923c', '#38bdf8', '#4ade80', '#facc15', '#f472b6'];

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const daysSince = (d) => { if (!d) return 9999; const x = new Date(d); return isNaN(x) ? 9999 : Math.floor((Date.now() - x) / 86400000); };
const healthOf = (days, t1, t2) => days > t2 ? 'red' : days > t1 ? 'yellow' : 'green';
const fmt = (n) => (n == null || isNaN(+n)) ? '—' : Number(n).toLocaleString('en-CA', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtDec = (n, d = 2) => (n == null || isNaN(+n)) ? '—' : Number(n).toLocaleString('en-CA', { minimumFractionDigits: d, maximumFractionDigits: d });
const pct = (a, b) => b ? ((a / b) * 100).toFixed(1) + '%' : '—';
// MySQL returns DATE/DATETIME columns as JS Date objects OR ISO strings — handle both.
const toDateStr = (v) => {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v) ? null : v.toISOString().slice(0, 10);
  const iso = new Date(String(v));
  return isNaN(iso.getTime()) ? null : iso.toISOString().slice(0, 10);
};
const inRange = (dateVal, s, e) => { const d = toDateStr(dateVal); return d ? d >= s && d <= e : false; };
const today = new Date().toISOString().slice(0, 10);
const yrStart = `${new Date().getFullYear()}-01-01`;
const latestOf = (arr, field) => {
  if (!arr.length) return null;
  const best = arr.reduce((a, b) => (toDateStr(a[field]) || '') >= (toDateStr(b[field]) || '') ? a : b);
  return toDateStr(best[field]);
};
// For production table: get the most recent date between Schedule_Date_Time and Production_Date_Time
const getProductionDate = (row) => {
  const schedDate = toDateStr(row.Schedule_Date_Time);
  const prodDate = toDateStr(row.Production_Date_Time);
  if (!schedDate && !prodDate) return null;
  if (!schedDate) return prodDate;
  if (!prodDate) return schedDate;
  return schedDate >= prodDate ? schedDate : prodDate;
};
const latestProductionDate = (arr) => {
  if (!arr.length) return null;
  const dates = arr.map(getProductionDate).filter(Boolean);
  if (!dates.length) return null;
  return dates.reduce((a, b) => a >= b ? a : b);
};
const supCost = (v) => (Number(v.UNIT_COST_INVOICE) || 0) === 0 ? Number(v.Unit_Cost_PO || 0) : Number(v.UNIT_COST_INVOICE || 0);
const rmCost = (v) => (Number(v.Unit_Cost_Invoice) || 0) === 0 ? Number(v.Unit_Cost_PO || 0) : Number(v.Unit_Cost_Invoice || 0);

// ─── MICRO COMPONENTS ────────────────────────────────────────────────────────

function DonutChart({ segments, size = 150, label, sub }) {
  const cx = size / 2, cy = size / 2, r = (size / 2) * 0.80, hole = r * 0.62;
  const total = segments.reduce((s, x) => s + (x.value || 0), 0);
  if (!total) return <div className="urd__empty">No data</div>;
  let cursor = -Math.PI / 2;
  const slices = segments.map((seg, i) => {
    const angle = (seg.value / total) * 2 * Math.PI;
    const x1 = cx + r * Math.cos(cursor), y1 = cy + r * Math.sin(cursor);
    cursor += angle;
    const x2 = cx + r * Math.cos(cursor), y2 = cy + r * Math.sin(cursor);
    const large = angle > Math.PI ? 1 : 0;
    return <path key={i} d={`M${cx} ${cy} L${x1} ${y1} A${r} ${r} 0 ${large} 1 ${x2} ${y2}Z`} fill={seg.color} opacity={0.92} />;
  });
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {slices}
      <circle cx={cx} cy={cy} r={hole} fill="#ffffff" />
      {label !== undefined && <text x={cx} y={cy - 4} textAnchor="middle" fill="#1a2e12" fontSize={size * .12} fontWeight="800" fontFamily="Plus Jakarta Sans,sans-serif">{label}</text>}
      {sub && <text x={cx} y={cy + size * .1} textAnchor="middle" fill="#5a7a4a" fontSize={size * .08} fontFamily="Plus Jakarta Sans,sans-serif">{sub}</text>}
    </svg>
  );
}

function BarChart({ rows, colorFn }) {
  if (!rows || !rows.length) return <div className="urd__empty">No data</div>;
  const max = Math.max(...rows.map(r => r.value), 1);
  return (
    <div className="urd__bar-group">
      {rows.map((row, i) => (
        <div className="urd__bar-row" key={i}>
          <div className="urd__bar-name" title={row.label}>{row.label}</div>
          <div className="urd__bar-track">
            <div className="urd__bar-fill" style={{ width: `${Math.max((row.value / max) * 100, 1)}%`, background: colorFn ? colorFn(i, row) : P[i % P.length] }}>
              {(row.value / max) > 0.28 && <span>{Math.round((row.value / max) * 100)}%</span>}
            </div>
          </div>
          <div className="urd__bar-val">{row.displayVal !== undefined ? row.displayVal : fmt(row.value)}</div>
        </div>
      ))}
    </div>
  );
}

function Ring({ pct: p = 0, color = '#6CA62A', size = 80, label }) {
  const r = (size / 2) - 8, circ = 2 * Math.PI * r, dash = (Math.min(p, 100) / 100) * circ;
  return (
    <div className="urd__ring-wrap">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#d6e6cc" strokeWidth="7" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="7"
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`} />
        <text x={size / 2} y={size / 2 + 2} textAnchor="middle" dominantBaseline="middle"
          fill="#1a2e12" fontSize={size * .18} fontWeight="800" fontFamily="Plus Jakarta Sans,sans-serif">
          {Math.round(p)}%
        </text>
      </svg>
      {label && <div className="urd__ring-label">{label}</div>}
    </div>
  );
}

function KPI({ label, value, sub, color = '#6CA62A', glyph, delay = '' }) {
  return (
    <div className={`urd__kpi urd__animate ${delay}`} style={{ '--urd-kpi-color': color }}>
      {glyph && <div className="urd__kpi-glyph">{glyph}</div>}
      <div className="urd__kpi-label">{label}</div>
      <div className="urd__kpi-value">{value}</div>
      {sub && <div className="urd__kpi-sub">{sub}</div>}
    </div>
  );
}

function Legend({ items }) {
  return (
    <div className="urd__donut-legend">
      {items.map((l, i) => (
        <div className="urd__legend-item" key={i}>
          <div className="urd__legend-dot" style={{ background: l.color }} />
          <span style={{ fontSize: 13 }}>{l.label}</span>
          <span className="urd__legend-pct">{l.val}</span>
        </div>
      ))}
    </div>
  );
}

function SectionLabel({ children }) {
  return <div className="urd__section-label urd__animate">{children}</div>;
}

function Card({ icon, title, badge, children, loading: isLoading, iconBg = '#e0f0d0' }) {
  return (
    <div className="urd__card">
      <div className="urd__card-header">
        <div className="urd__card-title">
          <div className="urd__card-icon" style={{ background: iconBg }}>{icon}</div>
          {title}
        </div>
        {badge && <span className="urd__chip">{badge}</span>}
      </div>
      <div className="urd__card-body">
        {isLoading
          ? <div className="urd__loading"><div className="urd__spinner" />Loading…</div>
          : children}
      </div>
    </div>
  );
}

function ShiftPill({ shift }) {
  return (
    <span className="urd__pill" style={{
      background: shift === 'Morning' ? '#fff3c0' : '#e0f0ff',
      color: shift === 'Morning' ? '#b06800' : '#1a5fa0',
      border: `1px solid ${shift === 'Morning' ? '#f0d080' : '#a0c8f0'}`,
    }}>
      {shift === 'Morning' ? '☀ Morning' : '🌙 Evening'}
    </span>
  );
}

function StatusPill({ status }) {
  const ok = status === 'Published';
  return (
    <span className="urd__pill" style={{
      background: ok ? '#e8f5d8' : '#fff3c0',
      color: ok ? '#6CA62A' : '#b06800',
      border: `1px solid ${ok ? '#c4dab6' : '#f0d080'}`,
    }}>
      {status || 'Draft'}
    </span>
  );
}

function Attachments({ raw, color = '#6CA62A' }) {
  try {
    const a = JSON.parse(raw || '[]');
    return a.length > 0
      ? <span style={{ color }}>{`✓ ${a.length}`}</span>
      : <span style={{ color: '#999' }}>—</span>;
  } catch { return <span style={{ color: '#999' }}>—</span>; }
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function UnifiedReportDashboard() {
  const token = getToken();
  const user = getUser();
  const qp = useMemo(() => ({ params: { token } }), [token]);

  const [startDate, setStartDate] = useState(yrStart);
  const [endDate, setEndDate] = useState(today);
  const [loading, setLoading] = useState(true);

  // ── RAW DATA (full tables — filtered client-side) ─────────────────────────
  const [rawRMLatest, setRawRMLatest] = useState([]);
  const [rawRMHistory, setRawRMHistory] = useState([]);
  const [rawSupLatest, setRawSupLatest] = useState([]);
  const [rawSupHistory, setRawSupHistory] = useState([]);
  const [rawExpenses, setRawExpenses] = useState([]);
  const [rawProduction, setRawProduction] = useState([]);
  const [rawShiftReports, setRawShiftReports] = useState([]);
  const [rawMaintReports, setRawMaintReports] = useState([]);
  const [rawPoTable, setRawPoTable] = useState([]);
  const [rawAttendance, setRawAttendance] = useState([]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      Axios.get(`${API}/po_raw_material_latest`, qp),
      Axios.get(`${API}/received_raw_material`, qp),
      Axios.get(`${API}/po_supplies`, qp),
      Axios.get(`${API}/received_supplies`, qp),
      Axios.get(`${API}/other_expenses`, qp),
      Axios.get(`${API}/production`, qp),
      Axios.get(`${API}/shift_reports`, qp),
      Axios.get(`${API}/maintenance_reports`, qp),
      Axios.get(`${API}/po_table`, qp),
      Axios.get(`${API}/attendance`, qp),
    ]).then(([rmL, rmH, supL, supH, exp, prod, shift, maint, po, att]) => {
      setRawRMLatest(Array.isArray(rmL.data) ? rmL.data : []);
      setRawRMHistory(Array.isArray(rmH.data) ? rmH.data : []);
      setRawSupLatest(Array.isArray(supL.data) ? supL.data : []);
      setRawSupHistory(Array.isArray(supH.data) ? supH.data : []);
      setRawExpenses(Array.isArray(exp.data) ? exp.data : []);
      setRawProduction(Array.isArray(prod.data) ? prod.data : []);
      setRawShiftReports(Array.isArray(shift.data) ? shift.data : []);
      setRawMaintReports(Array.isArray(maint.data) ? maint.data : []);
      setRawPoTable(Array.isArray(po.data) ? po.data : []);
      setRawAttendance(Array.isArray(att.data) ? att.data : []);
    }).catch(console.error).finally(() => setLoading(false));
  }, [qp]);

  const setRange = (days) => {
    const d = new Date(); d.setDate(d.getDate() - days);
    setStartDate(d.toISOString().slice(0, 10)); setEndDate(today);
  };

  // ── CLIENT-SIDE DATE FILTERING ────────────────────────────────────────────
  // Production: Use Date column for filtering (counting produced parts)
  const production = useMemo(() => rawProduction.filter(v => inRange(v.Date, startDate, endDate)), [rawProduction, startDate, endDate]);

  // Raw Material History: Use Receiving_Date for filtering
  const rmHistory = useMemo(() => rawRMHistory.filter(v => inRange(v.Receiving_Date, startDate, endDate)), [rawRMHistory, startDate, endDate]);

  // Supplies History: Use Receiving_Date for filtering
  const supHistory = useMemo(() => rawSupHistory.filter(v => inRange(v.Receiving_Date, startDate, endDate)), [rawSupHistory, startDate, endDate]);

  // Other Expenses: Use INVOICE_DATE, fallback to DATE if INVOICE_DATE is null
  const expenses = useMemo(() => rawExpenses.filter(v => {
    const d = toDateStr(v.INVOICE_DATE) || toDateStr(v.DATE);
    return d ? d >= startDate && d <= endDate : false;
  }), [rawExpenses, startDate, endDate]);

  // Shift Reports: Use date column for filtering
  const shiftReports = useMemo(() => rawShiftReports.filter(v => inRange(v.date, startDate, endDate)), [rawShiftReports, startDate, endDate]);

  // Maintenance Reports: Use date column for filtering
  const maintReports = useMemo(() => rawMaintReports.filter(v => inRange(v.date, startDate, endDate)), [rawMaintReports, startDate, endDate]);

  // PO Table: Use Timestamp column for filtering
  const poTable = useMemo(() => rawPoTable.filter(v => inRange(v.Timestamp, startDate, endDate)), [rawPoTable, startDate, endDate]);

  // Attendance: Use Date column for filtering (Status = 'Present'|'Absent'|'Late'|'Sick'|'Vacation')
  const attendance = useMemo(() => rawAttendance.filter(v => inRange(v.Date, startDate, endDate)), [rawAttendance, startDate, endDate]);

  // Raw Material Latest: Use PO_Date for filtering
  const rmLatest = useMemo(() => rawRMLatest.filter(v => inRange(v.PO_Date, startDate, endDate)), [rawRMLatest, startDate, endDate]);

  // Supplies Latest: Use PO_Date for filtering
  const supLatest = useMemo(() => rawSupLatest.filter(v => inRange(v.PO_Date, startDate, endDate)), [rawSupLatest, startDate, endDate]);

  // ── DERIVED — PRODUCTION ──────────────────────────────────────────────────
  const prod = useMemo(() => ({
    target: production.reduce((s, v) => s + Number(v.Target || 0), 0),
    produced: production.reduce((s, v) => s + Number(v.Produced || 0), 0),
    scrap: production.reduce((s, v) => s + Number(v.Scrap || 0), 0),
  }), [production]);
  const prodEff = prod.target ? (prod.produced / prod.target) * 100 : 0;
  const scrapRate = prod.produced ? (prod.scrap / prod.produced) * 100 : 0;

  const opMap = useMemo(() => production.reduce((a, v) => {
    const op = v.Operator || 'Unknown';
    if (!a[op]) a[op] = { produced: 0, target: 0, scrap: 0 };
    a[op].produced += Number(v.Produced || 0);
    a[op].target += Number(v.Target || 0);
    a[op].scrap += Number(v.Scrap || 0);
    return a;
  }, {}), [production]);

  const topOperators = Object.entries(opMap)
    .map(([name, d]) => ({ label: name, value: d.produced, displayVal: fmt(d.produced), eff: d.target ? d.produced / d.target : 0 }))
    .sort((a, b) => b.value - a.value).slice(0, 8);

  const partMap = useMemo(() => production.reduce((a, v) => {
    const p = v.Part_description || v.PartNo || 'Unknown';
    a[p] = (a[p] || 0) + Number(v.Produced || 0);
    return a;
  }, {}), [production]);
  const topParts = Object.entries(partMap)
    .map(([label, value]) => ({ label, value, displayVal: fmt(value) }))
    .sort((a, b) => b.value - a.value).slice(0, 8);

  // ── DERIVED — ATTENDANCE ──────────────────────────────────────────────────
  // DB: Status = 'Present' | 'Absent' | 'Late' | 'Sick' | 'Vacation'  (one row per employee per day)
  const attCounts = useMemo(() => attendance.reduce((acc, row) => {
    const s = (row.Status || '').toLowerCase();
    if (s === 'present') acc.present++;
    else if (s === 'absent') acc.absent++;
    else if (s === 'late') acc.late++;
    else if (s === 'sick') acc.sick++;
    else if (s === 'vacation') acc.vac++;
    return acc;
  }, { present: 0, absent: 0, late: 0, sick: 0, vac: 0 }), [attendance]);

  const totalTracked = attCounts.present + attCounts.absent;
  const attRate = totalTracked ? (attCounts.present / totalTracked) * 100 : 0;

  const absentByEmp = useMemo(() => attendance.reduce((acc, row) => {
    if ((row.Status || '').toLowerCase() !== 'absent') return acc;
    const name = row.Employee_Name || 'Unknown';
    acc[name] = (acc[name] || 0) + 1;
    return acc;
  }, {}), [attendance]);
  const topAbsent = Object.entries(absentByEmp)
    .map(([label, value]) => ({ label, value, displayVal: value + ' days' }))
    .sort((a, b) => b.value - a.value).slice(0, 8);

  // Per-department counts (recalculated from attendance rows)
  const deptData = useMemo(() => {
    const map = {};
    attendance.forEach(row => {
      const dept = row.Department || 'Unknown';
      if (!map[dept]) map[dept] = { present: 0, absent: 0, late: 0, sick: 0, vac: 0 };
      const s = (row.Status || '').toLowerCase();
      if (s === 'present') map[dept].present++;
      else if (s === 'absent') map[dept].absent++;
      else if (s === 'late') map[dept].late++;
      else if (s === 'sick') map[dept].sick++;
      else if (s === 'vacation') map[dept].vac++;
    });
    return map;
  }, [attendance]);

  // ── DERIVED — RAW MATERIAL RECEIVED ──────────────────────────────────────
  // Fields: Unit_Cost_Invoice, Unit_Cost_PO, QTY_Received, Supplier (confirmed from DB)
  const totalRM = useMemo(() => rmHistory.reduce((s, v) => s + rmCost(v) * Number(v.QTY_Received || 0), 0), [rmHistory]);
  const rmSupMap = useMemo(() => rmHistory.reduce((a, v) => {
    const s = v.Supplier || 'Unknown';
    a[s] = (a[s] || 0) + rmCost(v) * Number(v.QTY_Received || 0);
    return a;
  }, {}), [rmHistory]);
  const topRMSuppliers = Object.entries(rmSupMap)
    .map(([label, value]) => ({ label, value, displayVal: '$' + fmt(value) }))
    .sort((a, b) => b.value - a.value).slice(0, 6);

  // ── DERIVED — SUPPLIES RECEIVED ──────────────────────────────────────────
  // Fields: UNIT_COST_INVOICE (uppercase), Unit_Cost_PO, QTY_Received, Supplier (confirmed from DB)
  // NO Invoice_Amount column in Received_Supplies table — must calculate
  const totalSup = useMemo(() => supHistory.reduce((s, v) => s + supCost(v) * Number(v.QTY_Received || 0), 0), [supHistory]);
  const supSupMap = useMemo(() => supHistory.reduce((a, v) => {
    const s = v.Supplier || 'Unknown';
    a[s] = (a[s] || 0) + supCost(v) * Number(v.QTY_Received || 0);
    return a;
  }, {}), [supHistory]);
  const topSupSuppliers = Object.entries(supSupMap)
    .map(([label, value]) => ({ label, value, displayVal: '$' + fmt(value) }))
    .sort((a, b) => b.value - a.value).slice(0, 6);

  // ── DERIVED — OTHER EXPENSES ──────────────────────────────────────────────
  // Fields: INVOICE_DATE, Category, AMOUNT (confirmed from DB — NOT Invoice_Amount)
  const expByCat = useMemo(() => expenses.reduce((a, v) => {
    const c = v.Category || 'Other';
    a[c] = (a[c] || 0) + (Number(v.AMOUNT) || (Number(v.Unit_cost || 0) * Number(v.QTY || 0)));
    return a;
  }, {}), [expenses]);
  const totalExp = Object.values(expByCat).reduce((s, x) => s + x, 0);
  const topExpSuppliers = Object.entries(
    expenses.reduce((a, v) => { const s = v.Supplier || 'Unknown'; a[s] = (a[s] || 0) + Number(v.AMOUNT || 0); return a; }, {})
  ).map(([label, value]) => ({ label, value, displayVal: '$' + fmt(value) })).sort((a, b) => b.value - a.value).slice(0, 6);

  const totalSpend = totalExp + totalRM + totalSup;

  // ── DERIVED — PO TABLE ────────────────────────────────────────────────────
  // Fields: PO_NO, PART_NO, PART_DESCRIPTION, CUSTOMER, QTY, UNIT_PRICE, Timestamp (confirmed)
  const totalPOValue = useMemo(() => poTable.reduce((s, v) => s + Number(v.QTY || 0) * Number(v.UNIT_PRICE || 0), 0), [poTable]);
  const poByCustomer = useMemo(() => poTable.reduce((a, v) => {
    // CUSTOMER may be null or empty — skip blank rows in grouping
    const c = (v.CUSTOMER || '').trim() || 'Unknown';
    const lineVal = Number(v.QTY || 0) * Number(v.UNIT_PRICE || 0);
    if (lineVal > 0) a[c] = (a[c] || 0) + lineVal;
    return a;
  }, {}), [poTable]);
  const topPOCustomers = Object.entries(poByCustomer)
    .map(([label, value]) => ({ label, value, displayVal: '$' + fmt(value) }))
    .sort((a, b) => b.value - a.value).slice(0, 6);

  // ── HEALTH MONITOR ────────────────────────────────────────────────────────
  // NOTE: Health monitor uses different date columns than filtering in some cases:
  // - Production FILTERING uses Date column (for counting parts)
  // - Production HEALTH uses Schedule_Date_Time/Production_Date_Time (for data freshness)
  const healthItems = [
    {
      section: 'Production',
      lastDate: latestProductionDate(rawProduction),  // Uses Schedule_Date_Time / Production_Date_Time
      t: [1, 3],
      count: production.length,  // Filtered by Date column
      total: rawProduction.length
    },
    {
      section: 'Attendance',
      lastDate: latestOf(rawAttendance, 'Date'),  // Uses Date column
      t: [1, 3],
      count: attendance.length,
      total: rawAttendance.length
    },
    {
      section: 'PO Table',
      lastDate: latestOf(rawPoTable, 'Timestamp'),  // Uses Timestamp column
      t: [5, 14],
      count: poTable.length,
      total: rawPoTable.length
    },
    {
      section: 'Received RM',
      lastDate: latestOf(rawRMHistory, 'Receiving_Date'),  // Uses Receiving_Date column
      t: [3, 7],
      count: rmHistory.length,
      total: rawRMHistory.length
    },
    {
      section: 'Received Supplies',
      lastDate: latestOf(rawSupHistory, 'Receiving_Date'),  // Uses Receiving_Date column
      t: [3, 7],
      count: supHistory.length,
      total: rawSupHistory.length
    },
    {
      section: 'Other Expenses',
      lastDate: latestOf(rawExpenses.map(v => ({ ...v, _d: v.INVOICE_DATE || v.DATE })), '_d'),  // Uses INVOICE_DATE, fallback DATE
      t: [7, 21],
      count: expenses.length,
      total: rawExpenses.length
    },
    {
      section: 'Shift Reports',
      lastDate: latestOf(rawShiftReports, 'date'),  // Uses date column
      t: [1, 3],
      count: shiftReports.length,
      total: rawShiftReports.length
    },
    {
      section: 'Maintenance',
      lastDate: latestOf(rawMaintReports, 'date'),  // Uses date column
      t: [3, 7],
      count: maintReports.length,
      total: rawMaintReports.length
    },
  ];

  // ── DOWNLOAD ──────────────────────────────────────────────────────────────
  const handleDownload = () => {
    const now = new Date().toLocaleString();
    const healthRows = healthItems.map(h => {
      const days = daysSince(h.lastDate), status = h.lastDate ? healthOf(days, h.t[0], h.t[1]) : 'red';
      const dot = status === 'green' ? '🟢' : status === 'yellow' ? '🟡' : '🔴';
      const bg = status === 'red' ? '#fff5f5' : status === 'yellow' ? '#fffbf0' : '#f0fff8';
      const col = status === 'green' ? '#059669' : status === 'yellow' ? '#d97706' : '#dc2626';
      return `<tr style="background:${bg}"><td>${dot} ${h.section}</td><td>${h.lastDate ? h.lastDate.slice(0, 10) : 'No data'}</td><td style="font-weight:800;color:${col}">${h.lastDate ? days + 'd ago' : 'N/A'}</td><td>${h.count} / ${h.total}</td><td style="color:${col};font-weight:800;text-transform:uppercase">${status}</td></tr>`;
    }).join('');

    const barHtml = (rows, colorFn) => (rows || []).map((o, i) => `<div class="bar-row"><div class="bar-name">${o.label}</div><div class="bar-track"><div class="bar-fill" style="width:${rows[0].value ? Math.max(o.value / rows[0].value * 100, 1) : 0}%;background:${colorFn ? colorFn(i, o) : P[i % P.length]}">${rows[0].value ? Math.round(o.value / rows[0].value * 100) : 0}%</div></div><div class="bar-val">${o.displayVal !== undefined ? o.displayVal : fmt(o.value)}</div></div>`).join('');

    const expCatRows = Object.entries(expByCat).sort((a, b) => b[1] - a[1]).map(([c, v]) => `<tr><td>${c}</td><td><strong>$${fmt(v)}</strong></td><td>${pct(v, totalExp)}</td></tr>`).join('');
    const deptRows = Object.entries(deptData).map(([d, v]) => `<tr><td>${d}</td><td>${v.present}</td><td>${v.absent}</td><td>${pct(v.present, v.present + v.absent)}</td></tr>`).join('');

    const html = `<!DOCTYPE html><html><head><title>Operations Report — ${startDate} to ${endDate}</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;600;700;800&display=swap');
*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Plus Jakarta Sans',sans-serif;font-size:13px;color:#111;background:#fff;padding:36px 40px}
.cover{text-align:center;padding:48px 0 36px;border-bottom:3px solid #6CA62A;margin-bottom:40px}.cover h1{font-size:30px;font-weight:800;margin-bottom:8px}.cover p{color:#666}
h2{font-size:14px;font-weight:800;letter-spacing:.09em;text-transform:uppercase;margin:36px 0 16px;padding-bottom:8px;border-bottom:2px solid #6CA62A}
h3{font-size:11px;font-weight:700;color:#666;text-transform:uppercase;letter-spacing:.07em;margin:20px 0 10px}
.kpi-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:28px}
.kpi{border:1px solid #e5e7eb;border-radius:10px;padding:18px 16px;border-top:4px solid}
.kpi-val{font-size:28px;font-weight:800;line-height:1;margin:8px 0 4px}.kpi-lbl{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#888}.kpi-sub{font-size:11px;color:#aaa;margin-top:2px}
table{width:100%;border-collapse:collapse;margin-bottom:20px;font-size:12px}th{background:#f0f5ed;padding:9px 11px;text-align:left;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:.07em;color:#5a7a4a;border-bottom:2px solid #d6e6cc}td{padding:8px 11px;border-bottom:1px solid #f3f4f6}tr:hover td{background:#fafafa}
.bar-row{display:flex;align-items:center;gap:10px;margin-bottom:9px}.bar-name{width:140px;font-size:12px;color:#555;text-align:right;flex-shrink:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.bar-track{flex:1;background:#f3f4f6;border-radius:5px;height:20px;overflow:hidden}.bar-fill{height:100%;border-radius:5px;display:flex;align-items:center;padding-left:7px;font-size:10px;font-weight:700;color:#fff;min-width:2px}.bar-val{font-size:12px;font-weight:700;width:80px;text-align:right;flex-shrink:0;color:#333}
.two-col{display:grid;grid-template-columns:1fr 1fr;gap:28px;margin-bottom:24px}
footer{margin-top:48px;padding-top:16px;border-top:1px solid #e5e7eb;display:flex;justify-content:space-between;font-size:11px;color:#aaa}
</style></head><body>
<div class="cover"><h1>⬡ Operations Report</h1><p>Period: <strong>${startDate}</strong> → <strong>${endDate}</strong> &nbsp;·&nbsp; ${now} &nbsp;·&nbsp; By: ${user}</p></div>

<h2>⚑ Health</h2>
<table><tr><th>Section</th><th>Last Entry</th><th>Age</th><th>In Range / Total</th><th>Status</th></tr>${healthRows}</table>

<h2>🏭 Production</h2>
<div class="kpi-grid">
  <div class="kpi" style="border-top-color:#3b82f6"><div class="kpi-lbl">Target</div><div class="kpi-val" style="color:#3b82f6">${fmt(prod.target)}</div></div>
  <div class="kpi" style="border-top-color:#6CA62A"><div class="kpi-lbl">Produced</div><div class="kpi-val" style="color:#6CA62A">${fmt(prod.produced)}</div></div>
  <div class="kpi" style="border-top-color:#dc2626"><div class="kpi-lbl">Scrap</div><div class="kpi-val" style="color:#dc2626">${fmt(prod.scrap)}</div></div>
  <div class="kpi" style="border-top-color:#7c3aed"><div class="kpi-lbl">Efficiency</div><div class="kpi-val" style="color:#7c3aed">${prodEff.toFixed(1)}%</div><div class="kpi-sub">Scrap: ${scrapRate.toFixed(1)}%</div></div>
</div>
<div class="two-col">
  <div><h3>Top Operators</h3>${barHtml(topOperators, (i, o) => o.eff >= .9 ? '#6CA62A' : o.eff >= .7 ? '#d97706' : '#dc2626')}</div>
  <div><h3>Top Parts</h3>${barHtml(topParts)}</div>
</div>

<h2>👥 Attendance</h2>
<div class="kpi-grid">
  <div class="kpi" style="border-top-color:#6CA62A"><div class="kpi-lbl">Present</div><div class="kpi-val" style="color:#6CA62A">${attCounts.present}</div><div class="kpi-sub">Rate: ${attRate.toFixed(1)}%</div></div>
  <div class="kpi" style="border-top-color:#dc2626"><div class="kpi-lbl">Absent</div><div class="kpi-val" style="color:#dc2626">${attCounts.absent}</div></div>
  <div class="kpi" style="border-top-color:#d97706"><div class="kpi-lbl">Late</div><div class="kpi-val" style="color:#d97706">${attCounts.late}</div></div>
  <div class="kpi" style="border-top-color:#7c3aed"><div class="kpi-lbl">Sick / Vac</div><div class="kpi-val" style="color:#7c3aed">${attCounts.sick + attCounts.vac}</div></div>
</div>
<div class="two-col">
  <div><h3>Most Absent Employees</h3>${barHtml(topAbsent, () => '#f87171')}</div>
  <div><h3>By Department</h3><table><tr><th>Dept</th><th>Present</th><th>Absent</th><th>Rate</th></tr>${deptRows}</table></div>
</div>

<h2>💰 Financials</h2>
<div class="kpi-grid">
  <div class="kpi" style="border-top-color:#f59e0b"><div class="kpi-lbl">Other Expenses</div><div class="kpi-val" style="color:#f59e0b">$${fmt(totalExp)}</div></div>
  <div class="kpi" style="border-top-color:#3b82f6"><div class="kpi-lbl">RM Received</div><div class="kpi-val" style="color:#3b82f6">$${fmt(totalRM)}</div></div>
  <div class="kpi" style="border-top-color:#6CA62A"><div class="kpi-lbl">Supplies</div><div class="kpi-val" style="color:#6CA62A">$${fmt(totalSup)}</div></div>
  <div class="kpi" style="border-top-color:#7c3aed"><div class="kpi-lbl">Total Spend</div><div class="kpi-val" style="color:#7c3aed">$${fmt(totalSpend)}</div></div>
</div>
<div class="two-col">
  <div><h3>Expenses by Category</h3><table><tr><th>Category</th><th>Amount</th><th>Share</th></tr>${expCatRows}</table></div>
  <div><h3>Top RM Suppliers</h3>${barHtml(topRMSuppliers)}</div>
</div>

<h2>📦 Purchase Orders (${poTable.length})</h2>
<div class="kpi-grid">
  <div class="kpi" style="border-top-color:#3b82f6"><div class="kpi-lbl">Total POs</div><div class="kpi-val" style="color:#3b82f6">${poTable.length}</div></div>
  <div class="kpi" style="border-top-color:#6CA62A"><div class="kpi-lbl">Total Value</div><div class="kpi-val" style="color:#6CA62A">$${fmt(totalPOValue)}</div></div>
  <div class="kpi" style="border-top-color:#f59e0b"><div class="kpi-lbl">Customers</div><div class="kpi-val" style="color:#f59e0b">${Object.keys(poByCustomer).length}</div></div>
  <div class="kpi" style="border-top-color:#7c3aed"><div class="kpi-lbl">Avg Value</div><div class="kpi-val" style="color:#7c3aed">$${fmt(poTable.length ? totalPOValue / poTable.length : 0)}</div></div>
</div>
<h3>Top Customers</h3>${barHtml(topPOCustomers)}

<h2>📝 Shift Reports (${shiftReports.length})</h2>
<table><tr><th>Date</th><th>Supervisor</th><th>Shift</th><th>Status</th></tr>
${shiftReports.slice(0, 15).map(v => `<tr><td>${v.date ? v.date.slice(0, 10) : '—'}</td><td><strong>${v.supervisor || '—'}</strong></td><td>${v.shift || '—'}</td><td>${v.Status || '—'}</td></tr>`).join('')}
</table>

<h2>🔧 Maintenance Reports (${maintReports.length})</h2>
<table><tr><th>Date</th><th>Supervisor</th><th>Shift</th><th>Status</th></tr>
${maintReports.slice(0, 15).map(v => `<tr><td>${v.date ? v.date.slice(0, 10) : '—'}</td><td><strong>${v.supervisor || '—'}</strong></td><td>${v.shift || '—'}</td><td>${v.Status || '—'}</td></tr>`).join('')}
</table>

<footer><span>By: <strong>${user}</strong></span><span>${startDate} → ${endDate}</span><span>${now}</span></footer>
</body></html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `Operations_${startDate}_${endDate}.html`; a.click();
    URL.revokeObjectURL(url);
  };

  // ── RENDER ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="urd__root">
        <div className="urd__loading" style={{ minHeight: '100vh', flexDirection: 'column', gap: 16 }}>
          <div className="urd__spinner" style={{ width: 36, height: 36, borderWidth: 3 }} />
          <div>Loading all data…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="urd__root">

      {/* TOPBAR */}
      <div className="urd__topbar">
        <div className="urd__logo">
          <div className="urd__logo-icon">📊</div>
          <div>
            <div className="urd__logo-text">Operations Dashboard</div>
            <div className="urd__logo-sub">Master Report — All Modules</div>
          </div>
        </div>
        <div className="urd__topbar-right">
          <span className="urd__chip">👤 {user || '—'}</span>
          <button className="urd__btn urd__btn--download" onClick={handleDownload}>⬇ Download</button>
        </div>
      </div>

      <div className="urd__main">

        {/* CONTROLS */}
        <div className="urd__controls">
          <div className="urd__field">
            <span className="urd__label">Start Date</span>
            <input className="urd__input" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </div>
          <div className="urd__field">
            <span className="urd__label">End Date</span>
            <input className="urd__input" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </div>
          <div className="urd__presets">
            <button className="urd__btn urd__btn--ghost" onClick={() => { setStartDate(yrStart); setEndDate(today); }}>YTD</button>
            <button className="urd__btn urd__btn--ghost" onClick={() => setRange(90)}>90d</button>
            <button className="urd__btn urd__btn--ghost" onClick={() => setRange(30)}>30d</button>
            <button className="urd__btn urd__btn--ghost" onClick={() => setRange(7)}>7d</button>
            <button className="urd__btn urd__btn--ghost" onClick={() => setRange(0)}>Today</button>
          </div>
          <div style={{ marginLeft: 'auto', fontSize: 12, color: '#5a7a4a', display: 'flex', alignItems: 'flex-end' }}>
            {attendance.length} att · {production.length} prod · {rmHistory.length} RM · {supHistory.length} sup · {expenses.length} exp rows in range
          </div>
        </div>

        {/* ══ HEALTH ══ */}
        <SectionLabel>⚑ Section Activity Health</SectionLabel>
        <div className="urd__health-grid">
          {healthItems.map((h, i) => {
            const days = daysSince(h.lastDate), status = h.lastDate ? healthOf(days, h.t[0], h.t[1]) : 'red';
            return (
              <div key={i} className={`urd__health-item urd__health-item--${status} urd__animate urd__animate--d${Math.min(i + 1, 7)}`}>
                <div className="urd__health-name">{h.section}</div>
                <div className={`urd__health-badge urd__health-badge--${status}`}>
                  <span className={`urd__health-dot urd__health-dot--${status}`} />{status.toUpperCase()}
                </div>
                <div className={`urd__health-days urd__health-days--${status}`}>
                  {h.lastDate ? (days === 0 ? 'Today' : `${days}d`) : 'No Data'}
                </div>
                <div className="urd__health-meta">
                  {h.lastDate
                    ? <div className="urd__health-date">{h.lastDate.slice(0, 10)}</div>
                    : <div style={{ color: '#c02020', fontSize: 12, fontWeight: 700 }}>⚠ No entries</div>}
                  <div className="urd__health-count">{h.count} in range · {h.total} total</div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="urd__divider" />

        {/* ══ PRODUCTION ══ */}
        <SectionLabel>🏭 Production</SectionLabel>
        <div className="urd__grid-4">
          <KPI label="Target" value={fmt(prod.target)} color="#4f9eff" glyph="🎯" delay="urd__animate--d1" />
          <KPI label="Produced" value={fmt(prod.produced)} color="#6CA62A" glyph="✅" delay="urd__animate--d2" sub={`Efficiency: ${prodEff.toFixed(1)}%`} />
          <KPI label="Scrap" value={fmt(prod.scrap)} color="#d93025" glyph="♻" delay="urd__animate--d3" sub={`Scrap rate: ${scrapRate.toFixed(1)}%`} />
          <KPI label="Operators" value={Object.keys(opMap).length} color="#6f42c1" glyph="👷" delay="urd__animate--d4" sub={`${production.length} entries`} />
        </div>
        <div className="urd__grid-21 urd__animate">
          <Card icon="📊" title="Operator Output" badge="Top 8" iconBg="#e0f0d0">
            <BarChart rows={topOperators} colorFn={(i, r) => r.eff >= .9 ? '#6CA62A' : r.eff >= .7 ? '#e08a00' : '#d93025'} />
          </Card>
          <div className="urd__card">
            <div className="urd__card-header">
              <div className="urd__card-title"><div className="urd__card-icon" style={{ background: '#e0f0d0' }}>🥧</div>Output Mix</div>
            </div>
            <div className="urd__card-body">
              <div className="urd__donut-wrap" style={{ justifyContent: 'center', marginBottom: 20 }}>
                <DonutChart size={150} label={`${prodEff.toFixed(0)}%`} sub="efficiency"
                  segments={[
                    { value: prod.produced - prod.scrap, color: '#6CA62A' },
                    { value: prod.scrap, color: '#d93025' },
                    { value: Math.max(prod.target - prod.produced, 0), color: '#d6e6cc' },
                  ]} />
                <Legend items={[
                  { label: 'Good Output', color: '#6CA62A', val: fmt(prod.produced - prod.scrap) },
                  { label: 'Scrap', color: '#d93025', val: fmt(prod.scrap) },
                  { label: 'Gap to Target', color: '#d6e6cc', val: fmt(Math.max(prod.target - prod.produced, 0)) },
                ]} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                <Ring pct={prodEff} color="#6CA62A" size={84} label="Efficiency" />
                <Ring pct={100 - scrapRate} color="#4f9eff" size={84} label="Quality" />
              </div>
            </div>
          </div>
        </div>
        <div className="urd__card urd__card--full urd__animate">
          <div className="urd__card-header">
            <div className="urd__card-title"><div className="urd__card-icon" style={{ background: '#e0f0d0' }}>📋</div>Top Parts by Output</div>
            <span className="urd__chip">Top 8</span>
          </div>
          <div className="urd__card-body"><BarChart rows={topParts} /></div>
        </div>

        <div className="urd__divider" />

        {/* ══ ATTENDANCE ══ */}
        <SectionLabel>👥 Attendance</SectionLabel>
        <div className="urd__grid-4">
          <KPI label="Present" value={attCounts.present} color="#6CA62A" glyph="✅" delay="urd__animate--d1" sub={`Rate: ${attRate.toFixed(1)}%`} />
          <KPI label="Absent" value={attCounts.absent} color="#d93025" glyph="❌" delay="urd__animate--d2" />
          <KPI label="Late" value={attCounts.late} color="#e08a00" glyph="⏰" delay="urd__animate--d3" />
          <KPI label="Sick / Vacation" value={attCounts.sick + attCounts.vac} color="#6f42c1" glyph="🏖" delay="urd__animate--d4" sub={`Sick: ${attCounts.sick} · Vac: ${attCounts.vac}`} />
        </div>
        <div className="urd__grid-21 urd__animate">
          <Card icon="📊" title="Most Absent Employees" badge="Top 8" iconBg="#fde8e8">
            <BarChart rows={topAbsent} colorFn={() => '#d93025'} />
          </Card>
          <div className="urd__card">
            <div className="urd__card-header">
              <div className="urd__card-title"><div className="urd__card-icon" style={{ background: '#e0f0d0' }}>🥧</div>Status Breakdown</div>
            </div>
            <div className="urd__card-body">
              <div className="urd__donut-wrap" style={{ justifyContent: 'center', marginBottom: 20 }}>
                <DonutChart size={150} label={`${attRate.toFixed(0)}%`} sub="present"
                  segments={[
                    { value: attCounts.present, color: '#6CA62A' },
                    { value: attCounts.absent, color: '#d93025' },
                    { value: attCounts.sick, color: '#e08a00' },
                    { value: attCounts.vac, color: '#6f42c1' },
                    { value: attCounts.late, color: '#fb923c' },
                  ]} />
                <Legend items={[
                  { label: 'Present', color: '#6CA62A', val: attCounts.present },
                  { label: 'Absent', color: '#d93025', val: attCounts.absent },
                  { label: 'Sick', color: '#e08a00', val: attCounts.sick },
                  { label: 'Vacation', color: '#6f42c1', val: attCounts.vac },
                  { label: 'Late', color: '#fb923c', val: attCounts.late },
                ]} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Ring pct={attRate} color="#6CA62A" size={84} label="Attendance Rate" />
              </div>
            </div>
          </div>
        </div>

        {Object.keys(deptData).length > 0 && (
          <div className="urd__card urd__card--full urd__animate">
            <div className="urd__card-header">
              <div className="urd__card-title"><div className="urd__card-icon" style={{ background: '#e0f0d0' }}>🏢</div>Attendance by Department</div>
              <span className="urd__chip">{Object.keys(deptData).length} departments</span>
            </div>
            <div className="urd__card-body">
              <div className="urd__table-wrap">
                <table className="urd__table">
                  <thead><tr><th>Department</th><th>Present</th><th>Absent</th><th>Late</th><th>Sick</th><th>Vacation</th><th>Rate</th></tr></thead>
                  <tbody>
                    {Object.entries(deptData).sort((a, b) => b[1].present - a[1].present).map(([dept, d], i) => {
                      const dRate = (d.present + d.absent) ? (d.present / (d.present + d.absent) * 100) : 0;
                      return (
                        <tr key={i}>
                          <td style={{ fontWeight: 700 }}>{dept}</td>
                          <td style={{ color: '#6CA62A', fontWeight: 700 }}>{d.present}</td>
                          <td style={{ color: '#d93025', fontWeight: 700 }}>{d.absent}</td>
                          <td style={{ color: '#e08a00' }}>{d.late}</td>
                          <td style={{ color: '#e08a00' }}>{d.sick}</td>
                          <td style={{ color: '#6f42c1' }}>{d.vac}</td>
                          <td>
                            <span className="urd__pill" style={{
                              background: dRate >= 90 ? '#e8f5d8' : dRate >= 75 ? '#fff3c0' : '#fde8e8',
                              color: dRate >= 90 ? '#6CA62A' : dRate >= 75 ? '#b06800' : '#d93025',
                              border: `1px solid ${dRate >= 90 ? '#c4dab6' : dRate >= 75 ? '#f0d080' : '#f0b0b0'}`,
                            }}>{dRate.toFixed(1)}%</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <div className="urd__divider" />

        {/* ══ FINANCIALS ══ */}
        <SectionLabel>💰 Financials</SectionLabel>
        <div className="urd__grid-4">
          <KPI label="Other Expenses" value={'$' + fmt(totalExp)} color="#e08a00" glyph="🧾" delay="urd__animate--d1" sub={`${expenses.length} invoices`} />
          <KPI label="RM Received" value={'$' + fmt(totalRM)} color="#4f9eff" glyph="📦" delay="urd__animate--d2" sub={`${rmHistory.length} receipts`} />
          <KPI label="Supplies" value={'$' + fmt(totalSup)} color="#6CA62A" glyph="🔧" delay="urd__animate--d3" sub={`${supHistory.length} receipts`} />
          <KPI label="Total Spend" value={'$' + fmt(totalSpend)} color="#6f42c1" glyph="💵" delay="urd__animate--d4" />
        </div>
        <div className="urd__grid-21 urd__animate">
          <Card icon="📊" title="Expenses by Category" iconBg="#fef3e0">
            <BarChart rows={Object.entries(expByCat).sort((a, b) => b[1] - a[1]).map(([label, value]) => ({ label, value, displayVal: '$' + fmt(value) }))} />
          </Card>
          <div className="urd__card">
            <div className="urd__card-header">
              <div className="urd__card-title"><div className="urd__card-icon" style={{ background: '#fef3e0' }}>🥧</div>Spend Mix</div>
            </div>
            <div className="urd__card-body">
              <div className="urd__donut-wrap" style={{ justifyContent: 'center' }}>
                <DonutChart size={150} label={'$' + fmt(totalSpend)} sub="total"
                  segments={[{ value: totalExp, color: '#e08a00' }, { value: totalRM, color: '#4f9eff' }, { value: totalSup, color: '#6CA62A' }]} />
                <Legend items={[
                  { label: 'Other Expenses', color: '#e08a00', val: '$' + fmt(totalExp) },
                  { label: 'Raw Material', color: '#4f9eff', val: '$' + fmt(totalRM) },
                  { label: 'Supplies', color: '#6CA62A', val: '$' + fmt(totalSup) },
                ]} />
              </div>
            </div>
          </div>
        </div>
        <div className="urd__grid-2 urd__animate">
          <Card icon="🏭" title="Top RM Suppliers" badge="by cost" iconBg="#e0f0d0">
            <BarChart rows={topRMSuppliers} />
          </Card>
          <Card icon="🔧" title="Top Supplies Suppliers" badge="by cost" iconBg="#e0f0d0">
            <BarChart rows={topSupSuppliers} />
          </Card>
        </div>

        {/* Received Supplies detail table */}
        {supHistory.length > 0 && (
          <div className="urd__card urd__card--full urd__animate">
            <div className="urd__card-header">
              <div className="urd__card-title"><div className="urd__card-icon" style={{ background: '#e0f0d0' }}>🔧</div>Received Supplies Detail</div>
              <span className="urd__chip">{supHistory.length} records</span>
            </div>
            <div className="urd__card-body">
              <div className="urd__table-wrap">
                <table className="urd__table">
                  <thead>
                    <tr><th>PO No</th><th>Item No</th><th>Description</th><th>Supplier</th><th>PO QTY</th><th>Rec. QTY</th><th>Rec. Date</th><th>ETA</th><th>Currency</th><th>Unit Cost (PO)</th><th>Unit Cost (Inv)</th><th>Line Total</th><th>GRN No</th><th>Comments</th></tr>
                  </thead>
                  <tbody>
                    {supHistory.map((v, i) => {
                      const lineTotal = supCost(v) * Number(v.QTY_Received || 0);
                      return (
                        <tr key={i}>
                          <td style={{ fontFamily: 'JetBrains Mono,monospace', fontWeight: 600 }}>{v.PO_No}</td>
                          <td style={{ fontFamily: 'JetBrains Mono,monospace' }}>{v.Item_No}</td>
                          <td>{v.Item_Description}</td>
                          <td style={{ fontWeight: 600 }}>{v.Supplier}</td>
                          <td style={{ textAlign: 'right' }}>{v.PO_QTY}</td>
                          <td style={{ textAlign: 'right', fontWeight: 700, color: '#6CA62A' }}>{v.QTY_Received}</td>
                          <td style={{ fontFamily: 'JetBrains Mono,monospace' }}>{v.Receiving_Date ? v.Receiving_Date.slice(0, 10) : '—'}</td>
                          <td style={{ fontFamily: 'JetBrains Mono,monospace' }}>{v.ETA ? v.ETA.slice(0, 10) : '—'}</td>
                          <td>{v.PO_CURRENCY}</td>
                          <td style={{ textAlign: 'right', fontFamily: 'JetBrains Mono,monospace' }}>${fmtDec(v.Unit_Cost_PO)}</td>
                          <td style={{ textAlign: 'right', fontFamily: 'JetBrains Mono,monospace' }}>${fmtDec(v.UNIT_COST_INVOICE)}</td>
                          <td style={{ textAlign: 'right', fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>${fmt(lineTotal)}</td>
                          <td style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 11 }}>{v.GRN_No || '—'}</td>
                          <td style={{ color: '#5a7a4a', fontSize: 12 }}>{v.Comments || '—'}</td>
                        </tr>
                      );
                    })}
                    <tr className="urd__total-row">
                      <td colSpan={5} style={{ textAlign: 'right' }}>Totals</td>
                      <td style={{ textAlign: 'right' }}>{supHistory.reduce((s, v) => s + Number(v.QTY_Received || 0), 0)}</td>
                      <td colSpan={5}></td>
                      <td style={{ textAlign: 'right', color: '#6CA62A' }}>${fmt(totalSup)}</td>
                      <td colSpan={2}></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <div className="urd__divider" />

        {/* ══ PO TABLE ══ */}
        <SectionLabel>📦 Purchase Orders</SectionLabel>
        <div className="urd__grid-4">
          <KPI label="Total POs" value={poTable.length} color="#4f9eff" glyph="📋" delay="urd__animate--d1" />
          <KPI label="Total Value" value={'$' + fmt(totalPOValue)} color="#6CA62A" glyph="💵" delay="urd__animate--d2" />
          <KPI label="Customers" value={Object.keys(poByCustomer).length} color="#e08a00" glyph="🏢" delay="urd__animate--d3" />
          <KPI label="Avg PO Value" value={'$' + fmt(poTable.length ? totalPOValue / poTable.length : 0)} color="#6f42c1" glyph="📈" delay="urd__animate--d4" />
        </div>
        <div className="urd__grid-21 urd__animate">
          <Card icon="📊" title="Top Customers by PO Value" iconBg="#e0f0ff">
            <BarChart rows={topPOCustomers} />
          </Card>
          <div className="urd__card">
            <div className="urd__card-header">
              <div className="urd__card-title"><div className="urd__card-icon" style={{ background: '#e0f0ff' }}>🥧</div>Customer Mix</div>
            </div>
            <div className="urd__card-body">
              <div className="urd__donut-wrap" style={{ justifyContent: 'center' }}>
                <DonutChart size={150} label={fmt(poTable.length)} sub="orders"
                  segments={topPOCustomers.slice(0, 5).map((c, i) => ({ value: c.value, color: P[i % P.length] }))} />
                <Legend items={topPOCustomers.slice(0, 5).map((c, i) => ({ label: c.label, color: P[i % P.length], val: '$' + fmt(c.value) }))} />
              </div>
            </div>
          </div>
        </div>
        {poTable.length > 0 && (
          <div className="urd__card urd__card--full urd__animate">
            <div className="urd__card-header">
              <div className="urd__card-title"><div className="urd__card-icon" style={{ background: '#e0f0ff' }}>📋</div>PO Detail</div>
              <span className="urd__chip">{poTable.length} orders · ${fmt(totalPOValue)} total</span>
            </div>
            <div className="urd__card-body">
              <div className="urd__table-wrap">
                <table className="urd__table">
                  <thead><tr><th>PO No</th><th>Part No</th><th>Description</th><th>Customer</th><th>QTY</th><th>Unit Price</th><th>Line Total</th><th>Timestamp</th></tr></thead>
                  <tbody>
                    {poTable.slice(0, 50).map((v, i) => (
                      <tr key={i}>
                        <td style={{ fontFamily: 'JetBrains Mono,monospace', fontWeight: 600 }}>{v.PO_NO}</td>
                        <td style={{ fontFamily: 'JetBrains Mono,monospace' }}>{v.PART_NO}</td>
                        <td>{v.PART_DESCRIPTION}</td>
                        <td style={{ fontWeight: 600 }}>{v.CUSTOMER}</td>
                        <td style={{ textAlign: 'right' }}>{fmt(v.QTY)}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'JetBrains Mono,monospace' }}>${fmtDec(v.UNIT_PRICE)}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, color: '#4f9eff' }}>${fmt(Number(v.QTY || 0) * Number(v.UNIT_PRICE || 0))}</td>
                        <td style={{ fontFamily: 'JetBrains Mono,monospace' }}>{v.Timestamp ? v.Timestamp.slice(0, 10) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <div className="urd__divider" />

        {/* ══ SHIFT REPORTS ══ */}
        <SectionLabel>📝 Shift Reports</SectionLabel>
        <div className="urd__grid-3">
          <KPI label="Total" value={shiftReports.length} color="#5a7a4a" glyph="📄" delay="urd__animate--d1" />
          <KPI label="Morning" value={shiftReports.filter(r => r.shift === 'Morning').length} color="#e08a00" glyph="☀" delay="urd__animate--d2" />
          <KPI label="Evening" value={shiftReports.filter(r => r.shift === 'Evening').length} color="#4f9eff" glyph="🌙" delay="urd__animate--d3" />
        </div>
        {shiftReports.length > 0 && (
          <div className="urd__card urd__card--full urd__animate">
            <div className="urd__card-header">
              <div className="urd__card-title"><div className="urd__card-icon" style={{ background: '#e8f0e0' }}>📋</div>Shift Reports</div>
              <span className="urd__chip">{shiftReports.length} reports</span>
            </div>
            <div className="urd__card-body">
              <div className="urd__table-wrap">
                <table className="urd__table">
                  <thead><tr><th>Date</th><th>Supervisor</th><th>Shift</th><th>Status</th><th>Files</th></tr></thead>
                  <tbody>
                    {shiftReports.map((v, i) => (
                      <tr key={i}>
                        <td style={{ fontFamily: 'JetBrains Mono,monospace', fontWeight: 600 }}>{v.date ? v.date.slice(0, 10) : '—'}</td>
                        <td style={{ fontWeight: 700 }}>{v.supervisor}</td>
                        <td><ShiftPill shift={v.shift} /></td>
                        <td><StatusPill status={v.Status} /></td>
                        <td><Attachments raw={v.attachments} color="#6CA62A" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <div className="urd__divider" />

        {/* ══ MAINTENANCE ══ */}
        <SectionLabel>🔧 Maintenance Reports</SectionLabel>
        <div className="urd__grid-3">
          <KPI label="Total" value={maintReports.length} color="#e08a00" glyph="🔧" delay="urd__animate--d1" />
          <KPI label="Morning" value={maintReports.filter(r => r.shift === 'Morning').length} color="#6CA62A" glyph="☀" delay="urd__animate--d2" />
          <KPI label="Evening" value={maintReports.filter(r => r.shift === 'Evening').length} color="#4f9eff" glyph="🌙" delay="urd__animate--d3" />
        </div>
        {maintReports.length > 0 && (
          <div className="urd__card urd__card--full urd__animate">
            <div className="urd__card-header">
              <div className="urd__card-title"><div className="urd__card-icon" style={{ background: '#fef3e0' }}>📋</div>Maintenance Reports</div>
              <span className="urd__chip">{maintReports.length} reports</span>
            </div>
            <div className="urd__card-body">
              <div className="urd__table-wrap">
                <table className="urd__table">
                  <thead><tr><th>Date</th><th>Supervisor</th><th>Shift</th><th>Status</th><th>Files</th></tr></thead>
                  <tbody>
                    {maintReports.map((v, i) => (
                      <tr key={i}>
                        <td style={{ fontFamily: 'JetBrains Mono,monospace', fontWeight: 600 }}>{v.date ? v.date.slice(0, 10) : '—'}</td>
                        <td style={{ fontWeight: 700 }}>{v.supervisor}</td>
                        <td><ShiftPill shift={v.shift} /></td>
                        <td><StatusPill status={v.Status} /></td>
                        <td><Attachments raw={v.attachments} color="#e08a00" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* FOOTER */}
        <div style={{ borderTop: '1px solid #d6e6cc', paddingTop: 20, marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ fontSize: 14, color: '#5a7a4a' }}>
            By <strong style={{ color: '#1a2e12' }}>{user}</strong> &nbsp;·&nbsp; {startDate} → {endDate}
          </div>
          <button className="urd__btn urd__btn--download" onClick={handleDownload}>⬇ Download Summary Report</button>
        </div>

      </div>
    </div>
  );
}